// Shared types for the web SelfTestRunner. Mirrors iOS's `SVRTestResult`
// shape from `kaether_for_ios/Stoat/E2EE/SVRSelfTest.swift` so the two
// platforms can be cross-walked and a future CI step can diff results.

export interface TestCase {
  readonly name: string;
  readonly passed: boolean;
  /** Free-text diagnostic detail. Required when `passed` is false. */
  readonly detail?: string;
}

export interface TestSuiteResult {
  readonly allPassed: boolean;
  readonly cases: readonly TestCase[];
}

/**
 * A suite registered with the runner. Mirrors iOS `SelfTestRunner.Registration`.
 *
 * `isVerified` is the gating flag: a verified suite that regresses throws a
 * hard error in dev mode so the regression cannot be missed; an unverified
 * suite that fails just logs (used while the feature is still in `[~]` state
 * in `Signal_standard_web.md`).
 */
export interface RegisteredSuite {
  readonly name: string;
  readonly isVerified: boolean;
  readonly run: () => Promise<TestSuiteResult>;
}

export interface SelfTestSuiteRunResult {
  readonly name: string;
  readonly result: TestSuiteResult;
}

/**
 * Helper to build a `TestSuiteResult` from accumulated cases. Used by suite
 * authors to keep boilerplate minimal.
 */
export function buildSuiteResult(cases: readonly TestCase[]): TestSuiteResult {
  return {
    allPassed: cases.every((c) => c.passed),
    cases,
  };
}

/**
 * Helper to construct a passing case.
 */
export function pass(name: string, detail?: string): TestCase {
  return { name, passed: true, detail };
}

/**
 * Helper to construct a failing case. `detail` is mandatory here so suite
 * authors never log a bare "fail" with no diagnostic context.
 */
export function fail(name: string, detail: string): TestCase {
  return { name, passed: false, detail };
}
